/**
 * ULID generation utility
 *
 * ULID = Universally Unique Lexicographically Sortable Identifier
 * Format: 26 characters, Crockford's Base32 encoding
 * - First 10 chars: timestamp (milliseconds since Unix epoch)
 * - Last 16 chars: randomness
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const ENCODING_LEN = ENCODING.length
const TIME_LEN = 10
const RANDOM_LEN = 16

function encodeTime(now: number, len: number): string {
  let str = ''
  let remaining = now
  for (let i = len; i > 0; i--) {
    const mod = remaining % ENCODING_LEN
    str = ENCODING[mod]! + str
    remaining = Math.floor(remaining / ENCODING_LEN)
  }
  return str
}

function encodeRandom(len: number): string {
  let str = ''
  const randomBytes = new Uint8Array(len)
  crypto.getRandomValues(randomBytes)
  for (let i = 0; i < len; i++) {
    str += ENCODING[randomBytes[i]! % ENCODING_LEN]
  }
  return str
}

/**
 * Generate a new ULID
 *
 * @returns A 26-character ULID string
 *
 * @example
 * ```ts
 * const id = generateUlid()
 * // "01ARZ3NDEKTSV4RRFFQ69G5FAV"
 * ```
 */
export function generateUlid(): string {
  const timestamp = Date.now()
  return encodeTime(timestamp, TIME_LEN) + encodeRandom(RANDOM_LEN)
}

/**
 * Extract the timestamp from a ULID
 *
 * @returns Date object representing the ULID's creation time
 */
export function extractUlidTimestamp(ulid: string): Date {
  if (ulid.length !== 26) {
    throw new Error('Invalid ULID: must be 26 characters')
  }

  const timeStr = ulid.substring(0, TIME_LEN)
  let time = 0
  for (let i = 0; i < timeStr.length; i++) {
    const charIndex = ENCODING.indexOf(timeStr[i]!)
    if (charIndex === -1) {
      throw new Error(`Invalid ULID character: ${timeStr[i]}`)
    }
    time = time * ENCODING_LEN + charIndex
  }

  return new Date(time)
}
